/**
 * useApproveFlow — Approve & Proceed lifecycle.
 *
 * Encapsulates the next-agent + Step-8 + Step-9 branching from the
 * legacy `handleApprove`. Behavior matches the legacy contract:
 *
 *   - Step 9 (Final Report) — no-op. UI hides the button anyway.
 *   - Step 8 (last agent step) — calls `completeWorkflow` (no next
 *     agent to resume to).
 *   - Steps 1-7 — calls `resumeWorkflow` with `agentName =
 *     nextAgent(stepId)` (the resume target, not the agent being
 *     approved). Backend auto-marks the previous agent approved.
 *
 * On success, schedules a `setTimeout(refetch, 500)` re-hydrate so
 * the rail dot reflects the new backend state without waiting for
 * the regular polling tick.
 */
import { useCallback, useRef, useState } from 'react';
import { resumeWorkflow, completeWorkflow } from '../../../api/workflow';
import { findStep, nextAgent } from '../state/workflowV2StepCatalog';
import { notify } from '../../../services/notificationBus';
import { useMountedRef } from '../../../hooks/useMountedRef';
import type { ApproveFlowState } from '../step-components/stepBodyProps';
import type { WorkflowActions } from '../state/workflowV2Types';

const FINAL_AGENT_STEP_ID = 8;

export interface UseApproveFlowInput {
  stepId: number;
  threadId: string | null | undefined;
  sessionId: string | null | undefined;
  tenantId: string | null | undefined;
  refetchStatus: () => void;
  /** Optional — when present, the hook performs optimistic writes
   *  (current step → approved, next step → generating, selectStep
   *  forward) immediately on HTTP success, before the polling tick
   *  catches up. Closes the "blink" window where the Approve button
   *  briefly re-enables between the await and the polling refresh. */
  actions?: WorkflowActions;
  /** Story H8 — when true, the whole TARA is already done. Stale
   *  closures (e.g. from a rapid double-click before the button
   *  fully disables) shouldn't be able to POST /workflow/resume
   *  against an already-completed thread. */
  workflowCompleted?: boolean;
}

export function useApproveFlow({
  stepId,
  threadId,
  sessionId,
  tenantId,
  refetchStatus,
  actions,
  workflowCompleted,
}: UseApproveFlowInput): ApproveFlowState {
  const [approving, setApproving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Ref so the closure reads the latest refetch fn without
  // re-creating onApprove every polling tick.
  const refetchRef = useRef(refetchStatus);
  refetchRef.current = refetchStatus;
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  // Story H1 — gate post-await + setTimeout writes so they no-op
  // after unmount (e.g. user tab-switches between Approve click and
  // the 500ms refetch tick).
  const mountedRef = useMountedRef();

  const onApprove = useCallback(async () => {
    if (!threadId || !sessionId || !tenantId) {
      setError('Missing thread / session / tenant context.');
      return;
    }
    // Story H8 — defensive guard against post-completion approve.
    // Once the TARA is done there's nothing to resume. Without this,
    // a stale closure (rapid double-click race, or an
    // approveOverride chain that fires after polling has flipped
    // workflowCompleted) could POST /workflow/resume against an
    // already-completed thread.
    if (workflowCompleted) return;
    setApproving(true);
    setError(null);
    try {
      if (stepId === FINAL_AGENT_STEP_ID) {
        await completeWorkflow(threadId, sessionId);
        if (!mountedRef.current) return;
        // Optimistic: mark Step 8 as approved immediately. The
        // workflowCompleted flag derives from hydrate's pass over
        // all agent-backed steps, so polling will confirm shortly.
        actionsRef.current?.setStepStatus(stepId, 'approved');
      } else {
        const target = nextAgent(stepId);
        if (!target) throw new Error(`No next agent for step ${stepId}`);
        await resumeWorkflow({
          threadId,
          sessionId,
          tenantId,
          agentName: target,
          userFeedback: '',
        });
        if (!mountedRef.current) return;
        // Optimistic UI: close the blink window between HTTP success
        // and the next polling tick. Reducer's sticky-approved guard
        // protects the just-approved write from being demoted by the
        // first refetch (which may still report `awaiting_review`
        // due to backend write-lag). Polling reconciles cleanly
        // because the derived next states match these optimistic
        // writes.
        actionsRef.current?.setStepStatus(stepId, 'approved');
        // Story H9 — guard the optimistic write to `stepId + 1` via
        // the catalog. The reducer ignores writes to unknown slots
        // (no-op for non-existent step IDs) but a missing catalog
        // entry signals a misconfigured workflow — skip the
        // optimistic next-step transition rather than silently
        // dispatching to a phantom slot.
        const nextStep = findStep(stepId + 1);
        if (nextStep) {
          actionsRef.current?.setStepStatus(nextStep.id, 'generating');
          actionsRef.current?.selectStep(nextStep.id);
        }
      }
      // Hold the approving flag until the refetch fires so the
      // button never briefly re-enables. Mirrors the useReviseFlow
      // pattern. Mounted-ref gate prevents "state update on
      // unmounted component" if the user navigates away in the
      // 500ms window.
      setTimeout(() => {
        if (!mountedRef.current) return;
        refetchRef.current?.();
        setApproving(false);
      }, 500);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to approve step';
      if (!mountedRef.current) return;
      setError(msg);
      notify({ type: 'error', title: 'Approve failed', message: msg });
      setApproving(false);
    }
  }, [stepId, threadId, sessionId, tenantId, workflowCompleted]);

  return { approving, error, onApprove };
}
